import { Component, Input, OnInit } from '@angular/core';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { ServiceNoteItem } from '../../../../models/api/view/claim/details/case-management/service-note-response';
import { PhysicianAdvisorListItem } from '../../../../models/api/view/claim/details/case-management/physician-advisor-list-response';
import { TreatmentPlanListItem } from '../../../../models/api/view/claim/details/case-management/treatment-plan-list-response';
import { BaseComponent } from '../../../base/base.component';
import { BackButtonComponent } from '../../../core/buttons/back-button.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { CaseGeneralDetailComponent } from './case-general.component';
import { CaseNoteComponent } from './case-notes.component';
import { CasePeerReviewListComponent } from './peer-review-list.component';
import { CasePeerReviewComponent } from './peer-review.component';
import { CaseTreatmentPlanListComponent } from './treatment-plan-list.component';
import { CaseTreatmentPlanComponent } from './treatment-plan.component';

@Component({
  selector: 'app-case-management-detail',
  imports: [
    PageHeaderComponent,
    TabPanelComponent,
    TabComponent,
    ActionRowComponent,
    BackButtonComponent,
    CaseGeneralDetailComponent,
    CaseNoteComponent,
    CaseTreatmentPlanComponent,
    CaseTreatmentPlanListComponent,
    CasePeerReviewListComponent,
    CasePeerReviewComponent,
  ],
  template: `
    <div class="tw-pb-2">
      <app-action-row>
        <app-back-button
          left1
          (click)="view.navigateCurrentClaim(['case-management'])"
          >Back to case management</app-back-button
        >
      </app-action-row>
    </div>
    <app-page-header label="Case Management Detail" />
    <app-tab-panel (valueChange)="onTabChange($event)">
      <app-tab label="General" value="general">
        <ng-template #content>
          <app-case-general-detail
            [dataSource]="dataSource?.generalDetail"
            [loading]="generalDetailLoading"
          />
        </ng-template>
      </app-tab>
      <app-tab label="Notes" value="notes">
        <ng-template #content>
          <app-case-note [dataSource]="notesData" />
        </ng-template>
      </app-tab>
      <app-tab label="Treatment Plans" value="treatment">
        <ng-template #content>
          @if (treatmentPlanSelectedId) {
            <app-case-treatment-plan
              [treatmentPlanId]="treatmentPlanSelectedId"
              [treatmentPlanType]="treatmentPlanSelectedType"
              (backToList)="treatmentPlanSelectedId = null"
            />
          }
          <app-case-treatment-plan-list
            [dataSource]="treatmentPlanList"
            [hidden]="treatmentPlanSelectedId"
            [loading]="treatmentPlanLoading"
            (rowSelected)="onTreatmentPlanSelected($event)"
          />
        </ng-template>
      </app-tab>
      <app-tab label="Physician/Peer Review" value="physician">
        <ng-template #content>
          @if (peerReviewSelectedId) {
            <app-case-peer-review
              [physicianAdvisorId]="peerReviewSelectedId"
              (backToList)="peerReviewSelectedId = null"
            />
          }
          <app-case-peer-review-list
            [dataSource]="peerReviewList"
            [hidden]="peerReviewSelectedId"
            [loading]="peerReviewLoading"
            (rowSelected)="peerReviewSelectedId = $event.physicianAdvisorId"
          />
        </ng-template>
      </app-tab>
    </app-tab-panel>
  `,
  styles: [],
})
export class CaseManagementDetailComponent
  extends BaseComponent
  implements OnInit
{
  @Input() set dataSource(value: any) {
    this._dataSource = value ?? { generalDetail: null };
  }
  get dataSource(): any {
    return this._dataSource;
  }
  private _dataSource: any = { generalDetail: null };

  generalDetailLoaded = false;
  generalDetailLoading = true;
  notesData: ServiceNoteItem[] = [];
  notesLoaded = false;

  peerReviewList: PhysicianAdvisorListItem[] = [];
  peerReviewLoading = false;
  peerReviewLoaded = false;
  peerReviewSelectedId: number | null = null;

  treatmentPlanList: TreatmentPlanListItem[] = [];
  treatmentPlanLoading = false;
  treatmentPlanLoaded = false;
  treatmentPlanSelectedId: number | null = null;
  treatmentPlanSelectedType: 'UR' | 'Non-UR' = 'UR';

  ngOnInit(): void {
    this.loadGeneralDetail();
    this.view.hideLoading();
  }

  onTabChange(tabValue: string): void {
    if (tabValue === 'general' && !this.generalDetailLoaded) {
      this.loadGeneralDetail();
    }
    if (tabValue === 'notes' && !this.notesLoaded) {
      this.loadServiceNotes();
    }
    if (tabValue === 'physician' && !this.peerReviewLoaded) {
      this.loadPeerReviewList();
    }
    if (tabValue === 'treatment' && !this.treatmentPlanLoaded) {
      this.loadTreatmentPlanList();
    }
  }

  private async loadGeneralDetail(): Promise<void> {
    if (this.generalDetailLoaded) {
      return;
    }

    const serviceId = this.activatedRoute.snapshot.params['serviceId'];
    const claimKey = getClaimKey();

    try {
      this.dataSource.generalDetail =
        await this.api.caseManagement.getServiceDetail(
          claimKey,
          Number(serviceId),
        );
    } catch {
      this.dataSource.generalDetail = null;
    } finally {
      this.generalDetailLoaded = true;
      this.generalDetailLoading = false;
    }
  }

  private async loadServiceNotes(): Promise<void> {
    const serviceId = this.activatedRoute.snapshot.params['serviceId'];
    const claimKey = getClaimKey();

    try {
      const response = await this.api.caseManagement.getServiceNotes(
        claimKey,
        serviceId,
      );
      this.notesData = response.notes || [];
    } catch {
      this.notesData = [];
    } finally {
      this.notesLoaded = true;
    }
  }

  private async loadPeerReviewList(): Promise<void> {
    const serviceId = this.activatedRoute.snapshot.params['serviceId'];
    const claimKey = getClaimKey();

    this.peerReviewLoading = true;

    try {
      const response =
        await this.api.caseManagement.getPhysicianAdvisorList(claimKey);
      this.peerReviewList = (response.data || []).filter(
        (item) => item.serviceId === Number(serviceId),
      );
    } catch {
      this.peerReviewList = [];
    } finally {
      this.peerReviewLoading = false;
      this.peerReviewLoaded = true;
    }
  }

  private async loadTreatmentPlanList(): Promise<void> {
    const serviceId = this.activatedRoute.snapshot.params['serviceId'];
    const claimKey = getClaimKey();

    this.treatmentPlanLoading = true;

    try {
      const response =
        await this.api.caseManagement.getTreatmentPlanList(claimKey);
      this.treatmentPlanList = (response.data || []).filter(
        (item) => item.serviceId === Number(serviceId),
      );
    } catch {
      this.treatmentPlanList = [];
    } finally {
      this.treatmentPlanLoading = false;
      this.treatmentPlanLoaded = true;
    }
  }

  onTreatmentPlanSelected(item: TreatmentPlanListItem): void {
    this.treatmentPlanSelectedId = item.treatmentPlanId;
    this.treatmentPlanSelectedType = item.type || 'UR';
  }
}
